import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import withSizes from 'react-sizes';
import Grid from '@material-ui/core/Grid';
import { useLocation } from 'react-router-dom';
import Hidden from '@material-ui/core/Hidden';
import SeeAllLink from 'components/SeeAllLink';
import _isEmpty from 'lodash/isEmpty';
import EditorListItem from 'containers/EditorLists/components/EditorListItem';
import AppContainer from 'components/AppContainer';
import ListTitle from 'components/ListTitle';
import _get from 'lodash/get';
import { makeStyles } from '@material-ui/core/styles';
import {
  makeSelectClientDetails,
  makeSelectHomeHeaders,
} from 'containers/Main/selectors';
import { getContenfulFilters } from 'containers/Main/utils';
import ctf from 'utils/contentfulService';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: 14,
    [theme.breakpoints.down('480')]: {
      position: 'relative',
    },
  },
  viewMore: {
    display: 'flex',
    justifyContent: 'center',
  },
}));

const queryFn = async (
  _key,
  clientId,
  clientExcludedTopic,
  averageRatingCutoff,
  expertRatingCutoff,
  userRatingCutoff,
  isTopic,
) => {
  const contentfulFilters = getContenfulFilters(
    clientExcludedTopic,
    averageRatingCutoff,
    expertRatingCutoff,
    userRatingCutoff,
    isTopic,
  );
  const queries = [
    ctf.getEntries({
      content_type: 'list',
      'fields.reviewStatus': 'Accepted',
      'fields.client[exists]': 'false',
      limit: 4,
      include: 2,
      order: '-fields.calculatedAverageRating',
      ...contentfulFilters,
    }),
  ];
  if (clientId) {
    queries.push(
      ctf.getEntries({
        content_type: 'list',
        'fields.reviewStatus': 'Accepted',
        'fields.client.sys.id': clientId,
        limit: 4,
        include: 2,
        order: '-fields.calculatedAverageRating',
        ...contentfulFilters,
      }),
    );
  }
  const response = await Promise.all(queries);

  if (clientId)
    return response[1].items
      .concat(response[0].items)
      .sort(
        (a, b) =>
          (b.fields.calculatedAverageRating || 0) -
          (a.fields.calculatedAverageRating || 0),
      )
      .slice(0, 4);
  return response[0].items;
};

function EditorsList({ isMobile }) {
  const classes = useStyles();
  const location = useLocation();
  const homeHeaders = useSelector(makeSelectHomeHeaders());
  const clientDetails = useSelector(makeSelectClientDetails());

  const clientId = _get(clientDetails, 'sys.id');
  const clientExcludedTopic = _get(
    clientDetails,
    'excludeTopicCollection.items',
  );
  const averageRatingCutoff = _get(clientDetails, 'averageRatingCutoff');
  const expertRatingCutoff = _get(clientDetails, 'expertRatingCutoff');
  const userRatingCutoff = _get(clientDetails, 'userRatingCutoff');
  const queryInfo = useQuery(
    [
      'h-lists',
      clientId,
      clientExcludedTopic,
      averageRatingCutoff,
      expertRatingCutoff,
      userRatingCutoff,
      false,
    ],
    queryFn,
  );

  const { data, status } = queryInfo;
  const processing = status === 'loading';

  if (processing || _isEmpty(data)) {
    return null;
  }

  const title = _get(homeHeaders, 'pageCopy.editorLists');
  return (
    <AppContainer spacing={2} justify="start">
      <Grid item xs={12}>
        <Grid container direction="row" justify="space-between">
          <Grid item xs={12} sm={8}>
            <ListTitle title={title} />
          </Grid>
          <Hidden xsDown>
            <Grid item sm={4}>
              <SeeAllLink link="lists" />
            </Grid>
          </Hidden>
        </Grid>
      </Grid>
      {data
        .slice(0, isMobile ? 3 : 4)
        .map(
          item =>
            !_isEmpty(item) && (
              <EditorListItem
                item={item}
                key={item.fields.slug}
                location={location}
              />
            ),
        )}
      <Hidden smUp>
        <Grid item xs={12} className={classes.viewMore}>
          <SeeAllLink link="lists" isMobile bordered align="center" />
        </Grid>
      </Hidden>
    </AppContainer>
  );
}

EditorsList.propTypes = {
  isMobile: PropTypes.bool,
};

const mapSizesToProps = ({ width }) => ({
  isMobile: width < 480,
});

export default withSizes(mapSizesToProps)(EditorsList);
